"use client";

import React, { useState, useCallback, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ResponsiveModal } from "@west-tennessee-consulting/ui";
import { Bookmark, BookmarkCheck, Plus, X, Edit2, Trash2 } from "lucide-react";
import type {
  SimpleTableConfig,
  SimpleTableMobileSavedViewsProps,
  BaseEntity,
  SavedViewConfig,
} from "./types";
import type { SavedView as BaseSavedView } from "@/lib/types/saved-views";
import { savedViewToConfig } from "@/lib/hooks/api/saved-views";

// Extended SavedView interface for this component's specific needs
interface SavedView extends BaseSavedView {
  sortBy?: string;
  sortDirection?: "asc" | "desc";
  columns?: string[];
  isDefault?: boolean;
}

// ============================================================================
// MOBILE SAVED VIEWS COMPONENT
// ============================================================================

export function SimpleTableMobileSavedViews<T extends BaseEntity>({
  config,
  currentFilters,
  currentSortBy,
  currentSortDirection,
  onApplyView,
  onSaveView,
  onDeleteView,
  savedViews,
  isLoading = false,
}: SimpleTableMobileSavedViewsProps<T>) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [newViewName, setNewViewName] = useState("");
  const [editingView, setEditingView] = useState<SavedView | null>(null);

  // Check if current state matches any saved view
  const currentViewMatch = useMemo(() => {
    return savedViews.find(
      (view) =>
        JSON.stringify(view.filters) === JSON.stringify(currentFilters) &&
        view.sort?.field === currentSortBy &&
        view.sort?.direction === currentSortDirection
    );
  }, [savedViews, currentFilters, currentSortBy, currentSortDirection]);

  // Handle save current view
  const handleSaveCurrentView = useCallback(async () => {
    if (!newViewName.trim()) return;

    setIsSaving(true);
    try {
      await onSaveView(newViewName.trim(), {
        id: `temp-${Date.now()}`, // Temporary ID, will be replaced by server
        name: newViewName.trim(),
        filters: { ...currentFilters },
        sort: { field: currentSortBy || "", direction: currentSortDirection || "asc" },
        visibleColumns: [], // Could be expanded to include visible columns
      });
      setNewViewName("");
      setIsModalOpen(false);
    } catch (error) {
      console.error("Failed to save view:", error);
    } finally {
      setIsSaving(false);
    }
  }, [newViewName, currentFilters, currentSortBy, currentSortDirection, onSaveView]);

  // Handle apply view
  const handleApplyView = useCallback(
    (view: SavedViewConfig) => {
      onApplyView(view);
      setIsModalOpen(false);
    },
    [onApplyView]
  );

  // Handle delete view
  const handleDeleteView = useCallback(
    (viewId: string, event: React.MouseEvent) => {
      event.stopPropagation();
      onDeleteView(viewId);
    },
    [onDeleteView]
  );

  // Check if current state has changes
  const hasChanges = useMemo(() => {
    return (
      Object.keys(currentFilters).length > 0 ||
      currentSortBy !== config.defaultSort?.field ||
      currentSortDirection !== config.defaultSort?.direction
    );
  }, [currentFilters, currentSortBy, currentSortDirection, config.defaultSort]);

  if (!config.features?.savedViews) {
    return null;
  }

  return (
    <>
      {/* Saved Views Button */}
      <Button
        variant="outline"
        size="sm"
        onClick={() => setIsModalOpen(true)}
        className="flex items-center gap-2"
      >
        {currentViewMatch ? (
          <BookmarkCheck className="h-4 w-4 text-blue-600" />
        ) : (
          <Bookmark className="h-4 w-4" />
        )}
        <span className="hidden sm:inline">
          {currentViewMatch ? currentViewMatch.name : "Views"}
        </span>
        {hasChanges && (
          <Badge variant="secondary" className="ml-1">
            {Object.keys(currentFilters).filter((key) => currentFilters[key]).length}
          </Badge>
        )}
      </Button>

      {/* Saved Views Modal */}
      <ResponsiveModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        title="Saved Views"
        description={`Manage saved views for ${config.displayName}`}
        side="bottom"
        maxWidth="4xl"
      >
        <div className="space-y-4 p-4">
          {/* Save Current View */}
          {hasChanges && (
            <div className="border-b pb-4">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Save Current View</h3>
              <div className="flex gap-2">
                <Input
                  placeholder="View name..."
                  value={newViewName}
                  onChange={(e) => setNewViewName(e.target.value)}
                  className="flex-1"
                />
                <Button
                  onClick={handleSaveCurrentView}
                  disabled={!newViewName.trim() || isSaving}
                  size="sm"
                >
                  {isSaving ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                      Saving...
                    </div>
                  ) : (
                    <>
                      <Plus className="h-4 w-4 mr-2" />
                      Save
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Saved Views List */}
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Saved Views ({savedViews.length})</h3>

            {savedViews.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Bookmark className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No saved views yet</p>
                <p className="text-xs">Save your current filters and sorting as a view</p>
              </div>
            ) : (
              <div className="space-y-2">
                {savedViews.map((view) => (
                  <div
                    key={view.id}
                    className={`
                      flex items-center justify-between p-3 rounded-lg border cursor-pointer
                      transition-colors hover:bg-card-50
                      ${currentViewMatch?.id === view.id ? "border-blue-500 bg-blue-50 dark:bg-blue-950/40" : "border-gray-200 dark:border-gray-700"}
                    `}
                    onClick={() => handleApplyView(view)}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-card-foreground truncate">{view.name}</h4>
                        {view.isDefault && (
                          <Badge variant="outline" className="text-xs">
                            Default
                          </Badge>
                        )}
                        {currentViewMatch?.id === view.id && (
                          <Badge variant="default" className="text-xs">
                            Active
                          </Badge>
                        )}
                      </div>

                      <div className="text-xs text-muted-foreground mt-1">
                        {Object.keys(view.filters).filter((key) => view.filters[key]).length >
                          0 && (
                          <span>
                            {Object.keys(view.filters).filter((key) => view.filters[key]).length}{" "}
                            filter(s)
                          </span>
                        )}
                        {view.sort?.field && (
                          <span
                            className={
                              Object.keys(view.filters).filter((key) => view.filters[key]).length >
                              0
                                ? "ml-2"
                                : ""
                            }
                          >
                            Sorted by {view.sort.field} ({view.sort.direction})
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => handleDeleteView(view.id, e)}
                        className="h-8 w-8 p-0 text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-950/40" title="Delete"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="border-t pt-4">
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsModalOpen(false)}
                className="flex-1"
              >
                Close
              </Button>
              {currentViewMatch && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    // Reset to default view
                    onApplyView({
                      id: "default",
                      name: "Default View",
                      resource: config.entity,
                      filters: {},
                      sort: {
                        field: config.defaultSort?.field || "",
                        direction: config.defaultSort?.direction || "asc",
                      },
                      visibleColumns: [],
                      visibility: "private" as const,
                      shared_with_user_ids: [],
                      created_by: "",
                      created_at: new Date().toISOString(),
                      updated_at: new Date().toISOString(),
                    });
                    setIsModalOpen(false);
                  }}
                  className="flex-1"
                >
                  Reset to Default
                </Button>
              )}
            </div>
          </div>
        </div>
      </ResponsiveModal>
    </>
  );
}
